import * as React from "react";

import { CollectionPicker, DatasetPicker } from "@/shared/components/pickers";
import {
  ParameterField,
  useParameterField,
  useParameterValue,
} from "@/shared/components/ResourceForm";
import {
  type TemplateVariable,
  TemplateVariableType,
} from "@/shared/domain/agents";
import { Field, TextInput } from "@/shared/foundation/primitives";

export interface VariableRowProps {
  variable: TemplateVariable;
  /** What is wrong with the value, e.g. a required one left empty. */
  error?: string;
}

/**
 * One of the agent's variables, always a fixed value: a launch has no trigger
 * variables to offer. A row left alone shows the declared default and is not
 * sent; the server substitutes the default when the thread starts.
 */
export function VariableRow({ variable, error }: VariableRowProps) {
  const { value, isSet, displayText, write } = useParameterValue(
    "values",
    variable,
  );
  return (
    <ParameterField.Root
      parameter={variable}
      error={error}
      isSet={isSet}
      data-source="literal"
    >
      <ParameterField.Header />
      <ParameterField.Description />
      <ParameterField.Inputs>
        <ParameterField.Value>
          <VariableControl
            variable={variable}
            value={value}
            isSet={isSet}
            displayText={displayText}
            write={write}
          />
        </ParameterField.Value>
      </ParameterField.Inputs>
      <ParameterField.Error />
    </ParameterField.Root>
  );
}

interface VariableControlProps {
  variable: TemplateVariable;
  value: unknown;
  isSet: boolean;
  displayText: string;
  write: (next: string | undefined) => void;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function VariableControl({
  variable,
  value,
  isSet,
  displayText,
  write,
}: VariableControlProps) {
  const { labelId } = useParameterField();
  // A picker shows the declared default while unset, and a cleared pick
  // removes the key rather than storing null, so the default applies again.
  const shown = isSet ? stringOrNull(value) : stringOrNull(variable.default);
  const pick = (id: string | null) => {
    write(id ?? undefined);
  };

  switch (variable.type) {
    case TemplateVariableType.DATASET_ID:
      return (
        <DatasetPicker
          value={shown}
          onValueChange={pick}
          aria-labelledby={labelId}
        />
      );
    case TemplateVariableType.COLLECTION_ID:
      return (
        <CollectionPicker
          value={shown}
          resourceType={variable.collection_content_type ?? undefined}
          onValueChange={pick}
          aria-labelledby={labelId}
        />
      );
    default:
      return (
        <Field.Control
          render={<TextInput spellCheck={false} size="lg" />}
          value={displayText}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            write(event.target.value);
          }}
          aria-labelledby={labelId}
        />
      );
  }
}
